import { useCallback, useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";

function RolePick({ roles, value, onChange, edit, testid }) {
  return (
    <div className="flex flex-wrap gap-3" data-testid={testid}>
      {roles.map((r) => (
        <label key={r.key} className="flex items-center gap-1.5 text-[12px]">
          <Checkbox checked={value.includes(r.key)} disabled={!edit} data-testid={`${testid}-${r.key}`}
            onCheckedChange={(v) => onChange(v ? [...value, r.key] : value.filter((x) => x !== r.key))} />{r.name}
        </label>
      ))}
    </div>
  );
}

export function AiOzetSettings({ edit }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const load = useCallback(() => api.get("/ai-ozet/settings").then(({ data }) => { setD(data); setF(data.settings); }), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="panel h-32 animate-pulse" />;
  const save = async () => {
    try { await api.put("/ai-ozet/settings", { ...f, vade_threshold: Number(f.vade_threshold) }); toast.success("AI Özet ayarları kaydedildi"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <div className="panel p-4 space-y-4" data-testid="ai-settings-card">
      <h3 className="font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" />AI Özetler <span className="text-[11px] font-normal text-slate-500">model: {d.model}</span></h3>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-[13px] font-medium">Günlük Akıllı Özet</div>
          <div className="flex items-end gap-3">
            <label className="text-[12px] text-slate-600">Saat<Input type="time" value={f.daily_time} disabled={!edit} onChange={(e) => set("daily_time")(e.target.value)} data-testid="ai-daily-time" /></label>
            <label className="flex items-center gap-2 text-[13px] pb-2"><Switch checked={f.daily_enabled} disabled={!edit} onCheckedChange={set("daily_enabled")} data-testid="ai-daily-enabled" />Aktif</label>
          </div>
          <div className="text-[12px] text-slate-500">Alıcı roller</div>
          <RolePick roles={d.roles} value={f.daily_roles} onChange={set("daily_roles")} edit={edit} testid="ai-daily-roles" />
        </div>
        <div className="space-y-2">
          <div className="text-[13px] font-medium">Pazartesi Vade Raporu</div>
          <div className="flex items-end gap-3">
            <label className="text-[12px] text-slate-600">Saat (Pazartesi)<Input type="time" value={f.weekly_time} disabled={!edit} onChange={(e) => set("weekly_time")(e.target.value)} data-testid="ai-weekly-time" /></label>
            <label className="text-[12px] text-slate-600">Vade eşiği (gün)<Input type="number" min="1" max="365" value={f.vade_threshold} disabled={!edit} onChange={(e) => set("vade_threshold")(e.target.value)} data-testid="ai-vade-threshold" /></label>
            <label className="flex items-center gap-2 text-[13px] pb-2"><Switch checked={f.weekly_enabled} disabled={!edit} onCheckedChange={set("weekly_enabled")} data-testid="ai-weekly-enabled" />Aktif</label>
          </div>
          <div className="text-[12px] text-slate-500">Toplu raporu alan yönetici rolleri (her satış temsilcisi kendi raporunu alır)</div>
          <RolePick roles={d.roles} value={f.manager_roles} onChange={set("manager_roles")} edit={edit} testid="ai-manager-roles" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-[13px]"><Switch checked={f.email_enabled} disabled={!edit} onCheckedChange={set("email_enabled")} data-testid="ai-email-enabled" />Zamanlanmış raporları e-posta ile de gönder</label>
      {edit && <Button onClick={save} className="bg-violet-600 hover:bg-violet-700" data-testid="ai-settings-save">AI ayarlarını kaydet</Button>}
      <div data-testid="ai-usage-log">
        <div className="font-semibold text-[13px] mb-1">Son AI çağrıları (token kullanımı)</div>
        {d.usage.length === 0 ? <div className="text-[12px] text-slate-400">Henüz çağrı yok</div> : d.usage.map((u, i) => (
          <div key={i} className="text-[12px] text-slate-600 border-b border-slate-100 py-1">
            {formatDate(u.at)} · {u.kind === "gunluk" ? "Günlük" : "Vade"} · {u.status === "ok" ? "AI" : `yedek (${u.error || "hata"})`} · {u.total} token (girdi {u.input} / çıktı {u.output}){u.ms ? ` · ${(u.ms / 1000).toFixed(1)} sn` : ""} · {u.by}
          </div>))}
      </div>
    </div>
  );
}
